import { IPaymentIntentRequest as IPostAJobIntentBody } from '../../services/servicePostAJob/responseInterfaces';
import { IGetPaymentIntentPrepaidPurchaseRequest } from '../../services/servicePrepaidPurchase/interfacePrepaidPurchase';
import { IPaymentIntentRequest as IUpgradeIntentBody } from '../../services/serviceJobData/responseInterfaces';

/**
 * Explicit-shape builders for the four Jobs PaymentIntent request bodies.
 *
 * CONTRACT: every field the endpoint request model REQUIRES is always present
 * with an explicit, valid-typed value — never `undefined` at JSON.stringify time.
 * An undefined value drops the key, and the server then binds a default:
 *   • listingType → enum 0 → "Price has changed" hard fail (D-32/D-33)
 *   • standard/featuredPackagesQuantity → null → null totalPrice → StripeException (D-34)
 * Optional fields (couponCode) are INCLUDED ONLY WHEN PRESENT, matching each
 * flow's legacy submit-path wire behaviour (undefined coupon was dropped anyway).
 *
 * listingType is intentionally NOT coerced here — it is trusted from the
 * (#33-fixed) parent state plus the createIntent invariant guard. Quantities ARE
 * coalesced to 0 because 0 is a valid, meaningful "none of this pack" value.
 */

export interface IPostAJobIntentInput {
  listingType: number;
  siteId: string;
  standardPrice?: number | null;
  featuredPrice?: number | null;
  couponCode?: string;
}

export const buildPostAJobIntentBody = ({
  listingType,
  siteId,
  standardPrice,
  featuredPrice,
  couponCode,
}: IPostAJobIntentInput): IPostAJobIntentBody => ({
  listingType,
  siteId,
  standardPrice: standardPrice ?? null,
  featuredPrice: featuredPrice ?? null,
  ...(couponCode ? { couponCode } : {}),
});

export interface IReactivateIntentInput {
  listingType: number;
  siteId: string;
  standardPrice?: number | null;
  featuredPrice?: number | null;
}

// Reactivate reuses the Post-a-Job PI endpoint but never carries a coupon
// (the legacy Reactivate Step7 never passed couponCode).
export const buildReactivateIntentBody = ({
  listingType,
  siteId,
  standardPrice,
  featuredPrice,
}: IReactivateIntentInput): IPostAJobIntentBody => ({
  listingType,
  siteId,
  standardPrice: standardPrice ?? null,
  featuredPrice: featuredPrice ?? null,
});

export interface IPrepaidIntentInput {
  standardPackagesQuantity?: number;
  featuredPackagesQuantity?: number;
  siteId: string;
  couponCode?: string;
}

export const buildPrepaidIntentBody = ({
  standardPackagesQuantity,
  featuredPackagesQuantity,
  siteId,
  couponCode,
}: IPrepaidIntentInput): IGetPaymentIntentPrepaidPurchaseRequest => ({
  siteId,
  standardPackagesQuantity: standardPackagesQuantity ?? 0,
  featuredPackagesQuantity: featuredPackagesQuantity ?? 0,
  ...(couponCode ? { couponCode } : {}),
});

export interface IUpgradeIntentInput {
  siteId: string;
  eguid: string;
}

export const buildUpgradeIntentBody = ({
  siteId,
  eguid,
}: IUpgradeIntentInput): IUpgradeIntentBody => ({
  siteId,
  eguid,
});

/**
 * Interpret a PaymentIntent endpoint's 200 response body ({clientSecret, error}
 * contract — all four flows now aligned). A server-side graceful rejection
 * (e.g. total <= 0) returns `error` with no clientSecret; that must surface as the
 * intentError banner + Retry, never an infinite spinner. Precedence: server error
 * wins, else a clientSecret proceeds, else a generic fallback (so a malformed body
 * with neither still shows the banner instead of hanging).
 *
 * The transport-error branch (`'error' in result`) stays flow-specific in each
 * parent (e.g. Upgrade's 403 → ACCESS_REMOVED); this only covers the 200 body.
 */
export interface IIntentResponseData {
  clientSecret?: string;
  error?: string;
}

export interface IIntentOutcome {
  clientSecret: string | null;
  error: string | null;
}

export const GENERIC_INTENT_ERROR = 'We could not start payment. Please try again.';

export const interpretIntentResponse = (
  data: IIntentResponseData,
  fallbackError: string = GENERIC_INTENT_ERROR,
): IIntentOutcome => {
  if (data.error) {
    return { clientSecret: null, error: data.error };
  }
  if (data.clientSecret) {
    return { clientSecret: data.clientSecret, error: null };
  }
  return { clientSecret: null, error: fallbackError };
};
